import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { Button } from './ui/button';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading2, Heading3, List, ListOrdered, Quote, Link2, Image as ImageIcon,
  AlignLeft, AlignCenter, AlignRight, Undo2, Redo2, Code,
} from 'lucide-react';

/**
 * TipTapEditor — headless WYSIWYG editor used by the super admin to write
 * Insight articles. Exports HTML on `onChange(html)`.
 *
 * Props:
 *  - value: initial HTML string
 *  - onChange(html): fires on every content change
 *  - onImageUpload({ file }) => Promise<string>: optional custom upload
 *    hook (returns final image URL). If not provided, image inserts prompt
 *    the user for a URL.
 *  - placeholder
 *  - minHeight (px)
 */
export const TipTapEditor = ({
  value = '',
  onChange,
  onImageUpload,
  placeholder = 'Comece a escrever seu artigo…',
  minHeight = 300,
}) => {
  const editor = useEditor({
    extensions: [
      // StarterKit ships Link + Underline in recent versions; disable them to
      // avoid duplicate-extension warnings, then re-add with our custom config.
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false, underline: false }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-emerald-700 underline' } }),
      Image.configure({ inline: false, HTMLAttributes: { class: 'rounded-lg max-w-full my-4' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none px-4 py-3',
        style: `min-height: ${minHeight}px;`,
      },
    },
  });

  // Keep external value changes in sync (e.g., load draft after edit dialog opens)
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value && value !== current) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const addLink = () => {
    const previous = editor.getAttributes('link').href || '';
    const url = window.prompt('URL do link', previous);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const addImage = async () => {
    if (onImageUpload) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        try {
          const url = await onImageUpload({ file });
          if (url) editor.chain().focus().setImage({ src: url }).run();
        } catch (_) { /* handled by onImageUpload */ }
      };
      input.click();
      return;
    }
    const url = window.prompt('URL da imagem');
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const btn = (active, onClick, Icon, label, testId) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      data-testid={testId}
      className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors text-xs
        ${active
          ? 'bg-slate-900 text-white'
          : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
        }`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
        {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), Bold, 'Negrito (⌘B)', 'tt-bold')}
        {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), Italic, 'Itálico (⌘I)', 'tt-italic')}
        {btn(editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), UnderlineIcon, 'Sublinhado (⌘U)', 'tt-underline')}
        {btn(editor.isActive('strike'), () => editor.chain().focus().toggleStrike().run(), Strikethrough, 'Riscado')}
        <div className="w-px h-6 bg-slate-300 mx-1" />
        {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, 'Título')}
        {btn(editor.isActive('heading', { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), Heading3, 'Subtítulo')}
        {btn(editor.isActive('blockquote'), () => editor.chain().focus().toggleBlockquote().run(), Quote, 'Citação')}
        {btn(editor.isActive('codeBlock'), () => editor.chain().focus().toggleCodeBlock().run(), Code, 'Código')}
        <div className="w-px h-6 bg-slate-300 mx-1" />
        {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), List, 'Lista')}
        {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, 'Lista numerada')}
        <div className="w-px h-6 bg-slate-300 mx-1" />
        {btn(editor.isActive({ textAlign: 'left' }), () => editor.chain().focus().setTextAlign('left').run(), AlignLeft, 'Alinhar esquerda')}
        {btn(editor.isActive({ textAlign: 'center' }), () => editor.chain().focus().setTextAlign('center').run(), AlignCenter, 'Centralizar')}
        {btn(editor.isActive({ textAlign: 'right' }), () => editor.chain().focus().setTextAlign('right').run(), AlignRight, 'Alinhar direita')}
        <div className="w-px h-6 bg-slate-300 mx-1" />
        {btn(editor.isActive('link'), addLink, Link2, 'Link', 'tt-link')}
        {btn(false, addImage, ImageIcon, 'Imagem', 'tt-image')}
        <div className="w-px h-6 bg-slate-300 mx-1" />
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Desfazer"
        >
          <Undo2 className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Refazer"
        >
          <Redo2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <EditorContent editor={editor} data-testid="tiptap-editor" />
    </div>
  );
};
